'use strict'

/**
 * Main.js
 *
 * Bootstrap file for the complete frontend
 *
 * The application consists of an App object and Modules which are initialized through
 * the App object.
 *
 * App object (app.js)
 * -----------------
 *
 * Components:
 * -----------------
 * ?? PageComponent
 *
 * Modules
 * -----------------
 *
 * A module consists of an API object a marionette AppRouter and a couple of listeners which trigger <views> to show.
 *
 * The router is initialized with the given API and ties the routes to the API methods. The API methods
 * are responsible for triggering the different view controllers.
 *
 * Views
 * -----------------
 * A page is composed of different views and the views are grouped inside modules.
 * The view consists of a controller (index.js) the views (view.js) and associated templates in the templates folder.
 *
 * The controller is repsonsible for attaching the views to specific regions of a layout. This can be done in two ways.
 * 1. Call the 'showChildView' method on the main layout i.e ( this.layout.showChildView('systemRegion', systemLayout) )
 * 2. Trigger an App.globalChannel event tied to a Module API and give it a region to present itself in i.e ( App.globalChannel.trigger('devices:map', { region: this.layout.getRegion('mapRegion') }) )
 *
 * Start logic
 * -----------------
 * 1. on "DOMContentLoaded" the App is initialized
 * 2. on App start the system checks if a user is logged in and if the session is valid
 * if not the login screen is shown if valid the system is further bootstrapped by calling "app:bootstrap".
 * 3. during bootstrap a few necessary modules are initialized and at last the current url is reloaded and
 * views according to the url are rendered to the user
 *
 * Events
 * ------------------
 * devices:event:updated
 *
 * Components
 * -------------------
 * page-body-view: Standard page body wrapper. This represents a part below the navigation and the grey title area. Can show a list, title and a number of buttons
 *
 *
 * Page -> Modules map
 * -------------------
 * Login: /page-login
 * Status: /page-status
 *
 * Clients: /page-clients/list
 * Client: /page-clients/show
 * Client edit modal: /page-clients/edit_new
 * Client add modal: /page-clients/edit_new
 *
 * Devices: /page-devices/list
 * - Devices Filter: /components/devices-filter
 * - Devices List: /devices/list
 * - Devices Map: /devices/map
 * - Devices Groups: /groups/list
 *
 * Device: /devices/show
 * - Device status tab: /device-status
 * - Device situation tab: /device-situation
 * - Device events tab: /device-events
 * - Device schedule tab: /schedules/list
 *
 * Device edit modal (single|group|bulk): /devices/edit
 * Device control modal (single|group|bulk): /devices/control

 *
 *
 * Schedule list: /schedules/list
 * Schedule modal: /schedules/edit_new
 *
 *
 *
 * Add explanation about retrieving entities
 * App.globalChannel.request('device:geo:entities', {pagination: false})
 * Use pagination:false if you want to get the full data instead of the paginated data
 *
 * Explain something about the pagemodel
 *
 * Explain something about the collections
 */

/* make webpack process the stylesheets */
import '../sass/styles.scss'
//import '../assets/img/situation/default.jpg'

/* vendors */
import Backbone from 'backbone'
import 'backbone.syphon'
import 'backbone-validation'
// import 'backbone-relational'
// import 'backbone-fetch-cache'

import Marionette from 'marionette'
// import jQuery from 'jquery'
import async from 'async'

import Dialog from './common/dialogs'

/* app specific */
import Config from 'config'
import App from 'app'
import Socket from './socket'

/* Entities */
import SchedulesEntity from './entities/schedules'
import EmergenciesEntity from './entities/emergencies'
import ClientsEntity from './entities/clients'
import DevicesEntity from './entities/devices'
import StatisticsEntity from './entities/statistics'
import UsersEntity from './entities/users'
import EnumsEntity from './entities/enums'
import GroupsEntity from './entities/groups'
import HeadersEntity from './entities/headers'
import SessionEntity from './entities/session'
import EventsEntity from './entities/events'
import ApiKeysEntity from './entities/apikeys'
import MailReportsEntity from './entities/mailreports'
/**
 * App modules
 * -----------
 * These modules are later initialized and all have the same structure
 * < add some description about the structure of app modules >
 */
import Header from './modules/header'

import LoginModule from './modules/page-login'

import StyleGuideModule from './modules/styleguide'

import PageDevicesModule from './modules/page-devices'
import PageReportsModule from './modules/page-reports'
import PageStatusModule from './modules/page-status'
import PageClientsModule from './modules/page-clients'

import DevicesModule from './modules/devices'
import DeviceStatus from './modules/device-status'
import DeviceSituation from './modules/device-situation'
import DeviceEvents from './modules/device-events'

// import ClientModule from './modules/client'
import ClientPresets from './modules/client-presets'
import ClientNodes from './modules/client-nodes'
import ClientUsers from './modules/client-users'
import ClientGroups from './modules/client-groups'
import ClientApiKeys from './modules/client-apikeys'
import ClientMailReports from './modules/client-mailreports'

import Schedules from './modules/schedules'
import GroupsModule from './modules/groups'

// !! need to find a better place for the modal import
import Modal from '../node_modules/bootstrap-sass/assets/javascripts/bootstrap/modal.js'

/**
 * Basic App configuration
 */
App.config = Config
// A persistance cache to remember the filter settings
App.devicesFilterCache = {}

/* Dialogs (vex) */
App.dialog = Dialog

// App.dialog.alert('test-1')

/* PubSub Channel */
// Backbone.Radio.DEBUG = true
App.globalChannel = Backbone.Radio.channel('global')

/* Behaviors */
Marionette.Behaviors.behaviorsLookup = function () {
  return App.Behaviors
}
App.Behaviors = {}
App.Behaviors.Modal = require('./behaviors/modal-behavior.js')
App.TemplateHelpers = require('./common/template-helpers.js')
App.TextParser = require('./common/text-parser.js')
// App.TextParser('CONFIRM_PRESET_APPLY_DEVICE')
// App.TextParser('CONFIRM_PRESET_APPLY', {data: {ok: 'aa'}})
// enables to route based on permissions
App.PermissionAppRouter = require('./common/permission-app-router.js')

Backbone.Collection.prototype.filteredCollection = function (iterator) {
  // If you're using a collection that extends Backbone.Collection
  // the constructor will give you the same type of object back.
  return new this.constructor(this.filter(iterator))
}

/**
 * Initialize the app modules
 */
App.addModule('enumsEntity', EnumsEntity, {})
App.addModule('headersEntity', HeadersEntity, {})
App.addModule('groupsEntity', GroupsEntity, {})
App.addModule('schedulesEntity', SchedulesEntity, {})
App.addModule('emergenciesEntity', EmergenciesEntity, {})
App.addModule('eventsEntity', EventsEntity, {})
App.addModule('sessionEntity', SessionEntity, {})
App.addModule('apikeysEntity', ApiKeysEntity, {})
App.addModule('mailreportsEntity', MailReportsEntity, {})

App.addModule('clientsEntity', ClientsEntity, {})
App.addModule('devicesEntitity', DevicesEntity, {})
App.addModule('statisticsEntitity', StatisticsEntity, {})

App.addModule('usersEntitity', UsersEntity, {})
App.addModule('styleGuideModule', StyleGuideModule, {})
App.addModule('groupsModule', GroupsModule, {})
App.addModule('devices', DevicesModule, {})
App.addModule('pageClients', PageClientsModule, {})

App.addModule('schedules', Schedules, {})
App.addModule('pageDevices', PageDevicesModule, {})
App.addModule('pageReports', PageReportsModule, {})

App.addModule('pageStatus', PageStatusModule, {})

App.addModule('header', Header, {})
App.addModule('deviceStatus', DeviceStatus, {})
App.addModule('deviceEvents', DeviceEvents, {})
App.addModule('deviceSituation', DeviceSituation, {})
App.addModule('clientGroups', ClientGroups, {})
App.addModule('clientApiKeys', ClientApiKeys, {})
App.addModule('clientMailReports', ClientMailReports, {})
App.addModule('clientNodes', ClientNodes, {})
App.addModule('clientUsers', ClientUsers, {})
App.addModule('clientPresets', ClientPresets, {})

App.addModule('login', LoginModule, {})

/**
 * App Start
 * ---------
 * Verify the session and if successfull the bootstrap process is started if verify
 * fails the login screen will be shown.
 */
let onAppStart = function (options) {
  App.session = App.globalChannel.request('session:entity:new')

  // Verify the session
  App.session.verify(function (err) {
    if (Backbone.history) Backbone.history.start({silent: true})

    // if not logged in
    if (!App.session.get('logged_in')) {
      // jump to the login page and force a load
      App.globalChannel.trigger('app:navigate', 'login')
      Backbone.history.loadUrl()
    } else {
      App.globalChannel.trigger('app:bootstrap')
    }
  })
}
App.on('start', onAppStart)

/**
 * App bootstrap
 * -------------
 * Makes sure the clientsEntities and enumsEntities are loaded
 */
var bootstrap = function () {
  // initialize the modules
  async.each([
    'clientsEntity',
    'enumsEntity'
  ], function (moduleName, callback) {
    App.initializeModule(moduleName, callback)
  }, function (err) {
    if (err) {
      console.warn('Bootstrap failed')
      return false
    }
    App.globalChannel.trigger('app:start')

    // reload the url after bootstrap to retain access to freshly loaded resources
    Backbone.history.loadUrl()
  })
}

/** Hooks */
App.globalChannel.bind('app:start', function (options) {})
App.globalChannel.bind('app:bootstrap', function (options) {
  // if we enter the bootstrap and the url is login, this means we just logged in
  // so we can continue to the dashboard, otherwise don't take any action because it is just a browser
  // refresh
  if (Backbone.history.getFragment() === 'login') App.globalChannel.trigger('app:navigate', '/')
  bootstrap()
})
App.globalChannel.on('app:navigate', function (path, trigger) {
  Backbone.history.navigate(path, { trigger: (trigger || false) })
})

/**
 * Setup the app-layout this is the base for all the other modules
 */
import AppLayout from './components/app-layout/'
App.rootLayout = new AppLayout()
App.rootLayout.render()

/**
 * (1)
 * When DOM ready start the App, triggers the actual startup of the app
 */
document.addEventListener('DOMContentLoaded', function () {
  App.start()
})



// WEBPACK FOOTER //
// ./js/main.js